import { FC, useEffect, useMemo } from 'react';
import { Select, SelectOption, SelectValue } from '@netiva/classifieds-ui';

import { useAppDispatch, useAppSelector } from '@/store';
import { adActions } from '@/store/ad';
import { AdAttribute } from '@/store/ad/types';
import { useAd } from '@/pages/Ad/hooks';

export type SelectionInputProps = {
  attribute: AdAttribute;
  parentAttribute?: AdAttribute;
};

export const SelectionInput: FC<SelectionInputProps> = ({ attribute, parentAttribute }) => {
  const dispatch = useAppDispatch();
  const { formData } = useAppSelector((state) => state.ad);
  const { isNew, isLoading } = useAd();

  const parentEntries = useMemo(
    () => (parentAttribute ? formData[parentAttribute.id]?.entries : []) || [],
    [formData, parentAttribute]
  );

  const selectOptions = useMemo<SelectOption<number>[]>(() => {
    let filteredItems = attribute.items;
    if (parentAttribute) {
      filteredItems = filteredItems.filter((item) => !item.parentEntryId || parentEntries.includes(item.parentEntryId));
    }
    return filteredItems.map((o) => ({ label: o.name || '', value: o.id }));
  }, [attribute.items, parentAttribute, parentEntries]);

  function handleOnChange<IsMulti extends boolean>(value: SelectValue<number, IsMulti>) {
    dispatch(
      adActions.updateFormData({
        key: attribute.id,
        entries: value ? (typeof value === 'number' ? [value] : value) : [],
      })
    );
  }

  const isMultiSelect =
    attribute.type === 'SelectMultiAnd' ||
    attribute.type === 'SelectMultiOr' ||
    attribute.type === 'SelectMultiSearchSingle';

  const firstEntryAsDefault = !isMultiSelect && attribute.settings.FirstEntryAsSelectDefault?.toLowerCase() === 'true';
  const hasValue = !!formData[attribute.id]?.entries?.length;
  const firstOptionValue = selectOptions[0]?.value;

  // only a new ad is preselected, so an existing ad is never changed before its data is loaded
  useEffect(() => {
    if (firstEntryAsDefault && isNew && !isLoading && !hasValue && firstOptionValue !== undefined) {
      dispatch(adActions.updateFormData({ key: attribute.id, entries: [firstOptionValue] }));
    }
  }, [attribute.id, dispatch, firstEntryAsDefault, firstOptionValue, hasValue, isLoading, isNew]);

  return (
    <Select
      name={attribute.name || attribute.id.toString()}
      options={selectOptions}
      isMulti={isMultiSelect}
      isClearable={!firstEntryAsDefault}
      onChange={handleOnChange}
      value={isMultiSelect ? formData[attribute.id]?.entries : formData[attribute.id]?.entries?.[0]}
    />
  );
};
